/**
 * Conditional logging utility for development vs production
 * Provides structured logging with different levels and conditional output
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LoggerConfig {
  enabledInProduction: boolean;
  enabledLevels: LogLevel[];
  prefix?: string;
}

class Logger {
  private isDevelopment = import.meta.env.DEV;
  private config: LoggerConfig;

  constructor(config: Partial<LoggerConfig> = {}) {
    this.config = {
      enabledInProduction: false,
      enabledLevels: ['info', 'warn', 'error'],
      ...config
    };
  }

  private shouldLog(level: LogLevel): boolean {
    if (!this.isDevelopment && !this.config.enabledInProduction) {
      return level === 'error'; // Always log errors
    }
    return this.config.enabledLevels.includes(level);
  }

  private formatMessage(level: LogLevel, message: string, data?: any): any[] {
    const prefix = this.config.prefix ? `[${this.config.prefix}]` : '';
    const timestamp = new Date().toISOString().split('T')[1].split('.')[0];
    
    const args = [`${prefix} ${level.toUpperCase()} [${timestamp}] ${message}`];
    if (data !== undefined) {
      args.push(data);
    }
    return args;
  }

  debug(message: string, data?: any): void {
    if (this.shouldLog('debug')) {
      console.log(...this.formatMessage('debug', message, data));
    }
  }

  info(message: string, data?: any): void {
    if (this.shouldLog('info')) {
      console.info(...this.formatMessage('info', message, data));
    }
  }

  warn(message: string, data?: any): void {
    if (this.shouldLog('warn')) {
      console.warn(...this.formatMessage('warn', message, data));
    }
  }

  error(message: string, error?: any): void {
    if (this.shouldLog('error')) {
      console.error(...this.formatMessage('error', message, error));
    }
  }

  // Special method for API calls - only in development
  api(method: string, url: string, status?: number): void {
    if (this.isDevelopment) {
      const statusText = status ? `(${status})` : '';
      console.log(`🌐 API ${method} ${url} ${statusText}`);
    }
  }

  // Special method for auth operations - reduced verbosity
  auth(message: string, data?: any): void {
    if (this.isDevelopment) {
      console.log(`🔐 ${message}`, data ? data : '');
    }
  }
}

// Create logger instances for different components
export const createLogger = (prefix: string, config?: Partial<LoggerConfig>) => 
  new Logger({ ...config, prefix });

// Default loggers for common use cases
export const apiLogger = createLogger('API');
export const authLogger = createLogger('Auth');
export const copilotLogger = createLogger('Copilot');
export const documentsLogger = createLogger('Documents');

// Generic logger
export const logger = new Logger();